import { logAniOsChunkLoadError } from "@/utils/aniosDiagnostics";

/**
 * Prefetch OS app chunks so the first window open does not flash a full-pane loader.
 *
 * React.lazy still code-splits the route; calling the same dynamic `import()`
 * ahead of `openWindow` warms Vite's module cache.
 */

/** Loads a lazy OS app module. Return value is ignored. */
export type OsAppChunkLoader = () => Promise<unknown>;

/**
 * Optional scheduling hooks for idle prefetch. Tests inject these so idle
 * work can run without a real browser idle callback.
 */
export type OsAppPrefetchIdleHost = {
  requestIdleCallback?: (
    callback: (deadline: { timeRemaining: () => number }) => void,
    options?: { timeout: number },
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
  setTimeout?: (callback: () => void, delayMs: number) => number;
  clearTimeout?: (handle: number) => void;
};

/**
 * In-memory registry of OS app chunk loaders with deduped preload.
 */
export type OsAppPrefetchController = {
  /** Associate an OS app id with its dynamic import. */
  register: (appId: string, loader: OsAppChunkLoader) => void;
  /** Start loading one app chunk if a loader is registered. */
  preload: (appId: string) => Promise<void>;
  /** Start loading several app chunks. Unknown ids are ignored. */
  preloadMany: (appIds: readonly string[]) => void;
  /**
   * Load chunks during idle time, one app per slice.
   *
   * @returns Cancel function for unmount / dock id changes.
   */
  scheduleIdle: (
    appIds: readonly string[],
    host?: OsAppPrefetchIdleHost,
  ) => () => void;
  /** Drop loaders and in-flight promises. Used by unit tests. */
  reset: () => void;
};

const IDLE_TIMEOUT_MS = 2000;
const IDLE_FALLBACK_DELAY_MS = 1;

/**
 * Create an isolated prefetch registry. Production uses the shared singleton.
 *
 * @returns Controller with register / preload / idle scheduling.
 */
export function createOsAppPrefetchController(): OsAppPrefetchController {
  const loaders = new Map<string, OsAppChunkLoader>();
  const inFlight = new Map<string, Promise<void>>();

  const preload = (appId: string): Promise<void> => {
    const existing = inFlight.get(appId);
    if (existing) {
      return existing;
    }

    const loader = loaders.get(appId);
    if (!loader) {
      return Promise.resolve();
    }

    const pending = Promise.resolve(loader()).then(
      () => undefined,
      (error: unknown) => {
        inFlight.delete(appId);
        logAniOsChunkLoadError("prefetch", error, { appId });
      },
    );

    inFlight.set(appId, pending);
    return pending;
  };

  const preloadMany = (appIds: readonly string[]): void => {
    for (const appId of appIds) {
      void preload(appId);
    }
  };

  const scheduleIdle = (
    appIds: readonly string[],
    host?: OsAppPrefetchIdleHost,
  ): (() => void) => {
    const ids = appIds.filter((appId) => loaders.has(appId));
    let cancelled = false;
    let handle: number | undefined;
    let index = 0;

    const schedule = (
      callback: (deadline: { timeRemaining: () => number }) => void,
    ): number => {
      const requestIdle =
        host !== undefined
          ? host.requestIdleCallback
          : typeof requestIdleCallback === "function"
            ? requestIdleCallback.bind(globalThis)
            : undefined;

      if (requestIdle) {
        return requestIdle(callback, { timeout: IDLE_TIMEOUT_MS });
      }

      const startTimeout =
        host?.setTimeout ??
        ((cb: () => void, delayMs: number) =>
          globalThis.setTimeout(cb, delayMs) as unknown as number);
      return startTimeout(
        () => callback({ timeRemaining: () => 0 }),
        IDLE_FALLBACK_DELAY_MS,
      );
    };

    const cancelHandle = (scheduled: number): void => {
      const cancelIdle =
        host !== undefined
          ? host.cancelIdleCallback
          : typeof cancelIdleCallback === "function"
            ? cancelIdleCallback.bind(globalThis)
            : undefined;

      if (cancelIdle && host?.requestIdleCallback !== undefined) {
        cancelIdle(scheduled);
        return;
      }
      if (cancelIdle && host === undefined) {
        cancelIdle(scheduled);
        return;
      }

      const clear =
        host?.clearTimeout ??
        ((id: number) => {
          globalThis.clearTimeout(id);
        });
      clear(scheduled);
    };

    const run = (): void => {
      if (cancelled || index >= ids.length) {
        return;
      }
      void preload(ids[index]);
      index += 1;
      if (!cancelled && index < ids.length) {
        handle = schedule(run);
      }
    };

    if (ids.length > 0) {
      handle = schedule(run);
    }

    return () => {
      cancelled = true;
      if (handle !== undefined) {
        cancelHandle(handle);
      }
    };
  };

  return {
    register: (appId, loader) => {
      loaders.set(appId, loader);
    },
    preload,
    preloadMany,
    scheduleIdle,
    reset: () => {
      loaders.clear();
      inFlight.clear();
    },
  };
}

const sharedPrefetch = createOsAppPrefetchController();

/**
 * Register a dynamic import for an OS app id.
 *
 * @param appId - Window `appId` in `APP_REGISTRY`
 * @param loader - Dynamic `import()` for that app's window content
 */
export function registerOsAppLoader(
  appId: string,
  loader: OsAppChunkLoader,
): void {
  sharedPrefetch.register(appId, loader);
}

/**
 * Warm the JS chunk for an OS app if a loader is registered.
 *
 * @param appId - Window `appId` to prefetch
 */
export function preloadOsApp(appId: string): Promise<void> {
  return sharedPrefetch.preload(appId);
}

/**
 * Warm JS chunks for several OS apps.
 *
 * @param appIds - Window `appId`s to prefetch
 */
export function preloadOsApps(appIds: readonly string[]): void {
  sharedPrefetch.preloadMany(appIds);
}

/**
 * Prefetch OS app chunks during browser idle time.
 *
 * @param appIds - Window `appId`s to prefetch
 * @param host - Optional idle/timeout hooks (tests)
 * @returns Cancel function
 */
export function scheduleIdleOsAppPrefetch(
  appIds: readonly string[],
  host?: OsAppPrefetchIdleHost,
): () => void {
  return sharedPrefetch.scheduleIdle(appIds, host);
}

/**
 * Clear the shared prefetch registry. Unit tests only.
 */
export function resetOsAppPrefetchForTests(): void {
  sharedPrefetch.reset();
}
